import { Reference, StoreObject, useMutation } from "@apollo/client";
import { gql } from "generated-graphql";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useCallback } from "react";

const TOUCH_SUBMISSION_STEP = gql(`
  mutation TouchSubmissionStep($submissionId: ID!, $step: SubmissionStepType!, $status: SubmissionStepStatus) {
    touchSubmissionStep(submissionId: $submissionId, step: $step, status: $status) {
      step
      status
    }
  }
`);

/**
 * Touches a single submission stepper step, optimistically updating the cached
 * `Submission.stepperSteps` so the UI reflects the new status immediately.
 *
 * Navigation is intentionally left to the stepper shell — this hook only owns
 * the mutation and the cache write.
 */
export function useTouchSubmissionMutation(submissionId: string) {
  const [touchSubmissionStep, { loading }] = useMutation(TOUCH_SUBMISSION_STEP, {
    update: (cache, { data }) => {
      const touched = data?.touchSubmissionStep;
      if (!touched) {
        return;
      }
      cache.modify({
        id: cache.identify({
          __typename: "Submission",
          id: submissionId,
        }),
        fields: {
          // `SubmissionStepInfo` has no key fields, so entries are stored
          // inline (not as references). `readField` reads the `step` of each
          // entry whether it's stored as an object or a reference.
          stepperSteps: (existing: ReadonlyArray<Reference | StoreObject> = [], { readField }) =>
            existing.map((entry) =>
              readField("step", entry) === touched.step
                ? {
                    ...entry,
                    status: touched.status,
                  }
                : entry
            ),
        },
        optimistic: true,
        broadcast: true,
      });
    },
  });

  const touchStep = useCallback(
    async (
      step: SubmissionStepType,
      status: SubmissionStepStatus = SubmissionStepStatus.Completed
    ): Promise<void> => {
      await touchSubmissionStep({
        variables: {
          submissionId,
          step,
          status,
        },
      });
    },
    [touchSubmissionStep, submissionId]
  );

  return { touchStep, loading };
}
